import React, { useCallback, useEffect, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { CheckMark } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Button, Checkbox, LinkButton, Loader, Popover, Stack, Typography } from '@lyearn/ui';
import Search from '@/components/Filter/AsyncFilter/Search';
import UserDetail from '@/components/UsersSearch/components/UserDetail';
import useUsers from '@/components/UsersSearch/hooks/useUsers';
import VirtualizedList from '@/components/VirtualizedList';
import useDebounceState from '@/hooks/useDebounceState';
import { UserType } from '@/types/schema';

import NoResultsFound from '../NoResultsFound';
import SelectedUsers from './SelectedUsers';
import { popoverStyles, removePadding } from './styles';
import { UserMenuProps } from './types';

const UserMenu: React.FC<UserMenuProps> = ({
  groupIds,
  selectedUserIds,
  isDisabled,
  onChange,
  position = 'left-start',
  children,
  isSingleSelect = false,
}) => {
  const [isPopoverOpen, { toggle: togglePopover }] = useToggle();
  const { t } = useTranslation();
  const [searchInput, setSearchStr, debouncedSearchStr] = useDebounceState('');
  const {
    data: users,
    fetchMore,
    totalCount,
    isLoading,
    isFetchingMore,
  } = useUsers({
    searchText: debouncedSearchStr,
    groupIds,
  });
  const [checkedUserIds, setCheckedUserIds] = useState<string[]>(selectedUserIds ?? []);

  useEffect(() => {
    setCheckedUserIds(selectedUserIds ?? []);
  }, [selectedUserIds]);

  const noSearchResultsFound = users.length === 0 && debouncedSearchStr !== '';

  const onClickUser = useCallback(
    (userId: string) => {
      if (isSingleSelect) {
        onChange([userId]);
        togglePopover();
        return;
      }

      setCheckedUserIds((prev) => {
        if (prev.includes(userId)) {
          return prev.filter((id) => id !== userId);
        }
        return [...prev, userId];
      });
    },
    [isSingleSelect, onChange, togglePopover],
  );

  const onApplyClick = useCallback(() => {
    onChange(checkedUserIds);
    togglePopover();
  }, [checkedUserIds, onChange, togglePopover]);

  const renderItemContent = useCallback(
    (index: number, user: UserType) => {
      if (!user) {
        return <Box />;
      }

      if (index === 0 && !isSingleSelect) {
        return (
          <>
            <SelectedUsers
              checkedUserIds={checkedUserIds}
              selectedUserIds={selectedUserIds ?? []}
              onClick={onClickUser}
            />
            <Typography className="pl-8 text-text-secondary" variant="label-caps">
              All
            </Typography>
            <LinkButton
              className={cn('py-2 px-4', 'w-full text-start', removePadding)}
              onClick={() => onClickUser(user.id)}>
              <UserDetail
                key={user.id}
                endAdornment={
                  isSingleSelect ? (
                    selectedUserIds?.includes(user.id) ? (
                      <CheckMark fillColorToken="icon-secondary" height={16} width={16} />
                    ) : null
                  ) : (
                    <Checkbox checked={checkedUserIds?.includes(user.id)} />
                  )
                }
                showEmail={false}
                user={user}
              />
            </LinkButton>
          </>
        );
      }

      return (
        <LinkButton
          className={cn('py-2 px-4', 'w-full text-start', removePadding)}
          onClick={() => onClickUser(user.id)}>
          <UserDetail
            key={user.id}
            endAdornment={
              isSingleSelect ? (
                selectedUserIds?.includes(user.id) ? (
                  <CheckMark fillColorToken="icon-secondary" height={16} width={16} />
                ) : null
              ) : (
                <Checkbox checked={checkedUserIds?.includes(user.id)} />
              )
            }
            showEmail={false}
            user={user}
          />
        </LinkButton>
      );
    },
    [checkedUserIds, isSingleSelect, onClickUser, selectedUserIds],
  );

  return (
    <Popover open={isPopoverOpen} onOpenChange={togglePopover}>
      <Popover.Button disabled={isDisabled}>{children}</Popover.Button>
      <Popover.Content className={popoverStyles} position={position}>
        <Stack className="h-full p-8 pe-0" direction="col">
          <Stack className="z-10 mb-8 h-40 w-full">
            <Search
              search={{
                onChange: setSearchStr,
                value: searchInput,
                appearance: 'outline',
                placeholder: t('Common:SearchAssignee'),
              }}
            />
          </Stack>
          {isLoading ? (
            <Stack alignItems="center" className="h-full w-full flex-1" justifyContent="center">
              <Loader />
            </Stack>
          ) : noSearchResultsFound ? (
            <NoResultsFound searchText={searchInput} setSearchText={setSearchStr} />
          ) : (
            <>
              <VirtualizedList
                ListFooterComponent={
                  isFetchingMore ? <Loader className="mx-auto" size={24} /> : null
                }
                data={users}
                endReached={fetchMore}
                itemContent={renderItemContent}
                totalCount={totalCount}
              />
            </>
          )}
          {!isSingleSelect ? (
            <Stack className="w-full justify-end pt-8 pr-8">
              <Button shape="square" size="small" onClick={onApplyClick}>
                Apply
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </Popover.Content>
    </Popover>
  );
};

export default React.memo(UserMenu);
